/**
 * 简化版权限配置管理
 * 只使用两个核心环境变量控制权限系统
 */

// 权限配置接口
export interface PermissionConfig {
  /** 是否启用全局权限控制 */
  enabled: boolean;
  /** 是否使用后端权限模式 */
  useBackendPermission: boolean;
}

// 从环境变量获取配置
const getEnvConfig = (): PermissionConfig => {
  // 读取环境变量，提供默认值
  const enabled = process.env.REACT_APP_GLOBAL_PERMISSION_ENABLED !== 'false';
  const useBackendPermission =
    process.env.REACT_APP_USE_BACKEND_PERMISSION !== 'false';

  return {
    enabled,
    useBackendPermission,
  };
};

/**
 * 权限配置管理类
 */
export class PermissionConfigManager {
  private static instance: PermissionConfigManager;
  private config: PermissionConfig;
  private localStorageKey = 'permission_config';

  private constructor() {
    this.config = this.loadConfig();
  }

  public static getInstance(): PermissionConfigManager {
    if (!PermissionConfigManager.instance) {
      PermissionConfigManager.instance = new PermissionConfigManager();
    }
    return PermissionConfigManager.instance;
  }

  /**
   * 加载配置（优先从 localStorage，其次使用环境变量）
   */
  private loadConfig(): PermissionConfig {
    try {
      // 首先尝试从 localStorage 读取
      const savedConfig = localStorage.getItem(this.localStorageKey);
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig);
        // 验证配置结构
        if (
          typeof parsedConfig.enabled === 'boolean' &&
          typeof parsedConfig.useBackendPermission === 'boolean'
        ) {
          return {
            ...getEnvConfig(), // 环境变量作为基础
            ...parsedConfig, // localStorage 覆盖
          };
        }
      }
    } catch (_error) {
      // 加载失败，使用环境变量配置
    }

    // 使用环境变量配置
    return getEnvConfig();
  }

  /**
   * 获取当前配置
   */
  public getConfig(): PermissionConfig {
    return { ...this.config };
  }

  /**
   * 更新配置
   */
  public updateConfig(newConfig: Partial<PermissionConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };

    // 保存到 localStorage
    this.saveConfig();

    // 触发配置变更回调
    this.notifyConfigChange();
  }

  /**
   * 保存配置到 localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.config));
    } catch (error) {
      console.error('保存权限配置失败:', error);
    }
  }

  /**
   * 重置为环境变量配置
   */
  public resetToEnvConfig(): void {
    this.config = getEnvConfig();
    localStorage.removeItem(this.localStorageKey);
    this.notifyConfigChange();
  }

  /**
   * 检查是否启用权限控制
   */
  public isPermissionEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * 检查是否使用后端权限模式
   */
  public shouldUseBackendPermission(): boolean {
    return this.config.useBackendPermission;
  }

  /**
   * 获取权限模式描述
   */
  public getPermissionMode(): 'backend' | 'local' {
    return this.config.useBackendPermission ? 'backend' : 'local';
  }

  /**
   * 配置变更回调函数数组
   */
  private configChangeCallbacks: Array<() => void> = [];

  /**
   * 注册配置变更回调
   */
  public onConfigChange(callback: () => void): void {
    this.configChangeCallbacks.push(callback);
  }

  /**
   * 触发配置变更通知
   */
  private notifyConfigChange(): void {
    this.configChangeCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('配置变更回调执行失败:', error);
      }
    });
  }

  /**
   * 获取配置摘要信息（用于调试）
   */
  public getConfigSummary(): any {
    return {
      enabled: this.config.enabled,
      useBackendPermission: this.config.useBackendPermission,
      mode: this.config.useBackendPermission ? '后端权限模式' : '本地路由模式',
      source:
        'localStorage' in window && localStorage.getItem(this.localStorageKey)
          ? 'localStorage'
          : 'environment',
    };
  }
}

// 导出单例实例
export const permissionConfigManager = PermissionConfigManager.getInstance();

// 便捷的 hooks
export const usePermissionConfig = () => {
  return permissionConfigManager;
};

// 便捷的配置检查函数
export const isPermissionEnabled = (): boolean => {
  return permissionConfigManager.isPermissionEnabled();
};

export const shouldUseBackendPermission = (): boolean => {
  return permissionConfigManager.shouldUseBackendPermission();
};

export const getPermissionMode = (): 'backend' | 'local' => {
  return permissionConfigManager.getPermissionMode();
};
